import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import { login as loginAPI } from '../../api/authAPI';
import { fetchUser } from '../user/userSlice';

const initialState = {
  token: null,
  isAuthenticated: false,
  loading: false,
  error: null,
  userID: null,
  requiresVerification: false,
};

export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { dispatch, rejectWithValue }) => {
    try {
      const response = await loginAPI({ email, password });
      const res = response.data;
      //  CASE 1: Email not verified
      if (res?.requires_verification && !res?.isEmailVerified) {
        return rejectWithValue({
          message: res.message || 'Please verify your email to continue.',
          email: res.email,
          userID: res.userID,
          requires_verification: true,
        });
      }

      //  CASE 2: Normal login (verified user)
      const token = res?.token;
      if (!token) {
        // This now only runs for verified users missing a token
        return rejectWithValue({
          message: 'Token not found in response',
        });
      }

      localStorage.setItem('authToken', token);

      await dispatch(fetchUser());
      return { token };
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        'Invalid email or password';
      return rejectWithValue({ message });
    }
  }
);

// 🔹 Restore session on app load
export const restoreSession = createAsyncThunk(
  'auth/restoreSession',
  async (_, { dispatch, rejectWithValue }) => {
    const token = localStorage.getItem('authToken');
    if (!token) return rejectWithValue('No token found');

    try {
      await dispatch(fetchUser());
      return { token };
    } catch (error) {
      return rejectWithValue('Session expired or invalid');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.token = null;
      state.isAuthenticated = false;
      state.error = null;
      state.requiresVerification = false;
      localStorage.removeItem('authToken');
    },
    clearError: (state) => {
      state.error = null;
    },
    resetLoading: (state) => {
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // 🔸 LOGIN
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.requiresVerification = false;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || action.payload || null;

        // Special case: email not verified
        if (action.payload?.requires_verification) {
          state.requiresVerification = true;
          state.userID = action.payload.userID;
        }
      })

      // 🔸 RESTORE SESSION
      .addCase(restoreSession.pending, (state) => {
        state.loading = true;
      })
      .addCase(restoreSession.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
      })
      .addCase(restoreSession.rejected, (state, action) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.token = null;
        state.error = action.payload;
        localStorage.removeItem('authToken');
      });
  },
});

export const { logout, clearError, resetLoading } = authSlice.actions;
export default authSlice.reducer;

// Selectors
export const selectAuth = (state) => state.auth;

export const getUserIsAuthenticated = createSelector(
  [selectAuth],
  (auth) => auth.isAuthenticated
);
